// 🛡️ Project: LearningPortal
// 📅 Created: 2025
// 👤 Author: Dmitriy P.A.
// 🔒 Proprietary Code – do not copy without permission.

import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Strike from "@tiptap/extension-strike";
import Highlight from "@tiptap/extension-highlight";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import Document from "@tiptap/extension-document";
import Mention from "@tiptap/extension-mention";
import Suggestion from "@tiptap/suggestion";
import { mergeAttributes, Mark } from "@tiptap/core";
import axios from "axios";
import "./styles/TiptapEditor.css";

const FontSize = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: (element) => element.style.fontSize?.replace(/['\"]+/g, ""),
        renderHTML: (attributes) => {
          if (!attributes.fontSize) return {};
          return { style: `font-size: ${attributes.fontSize}` };
        },
      },
    };
  },
});

const Uppercase = Mark.create({
  name: "uppercase",
  addOptions() {
    return { HTMLAttributes: { style: "text-transform: uppercase;" } };
  },
  addCommands() {
    return {
      toggleUppercase:
        () =>
        ({ commands }) =>
          commands.toggleMark("uppercase"),
    };
  },
  parseHTML() {
    return [
      {
        tag: "span",
        getAttrs: (node) =>
          typeof node === "object" && node.style?.textTransform === "uppercase"
            ? {}
            : false,
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

const TiptapEditor = ({ apiUrl, token, initialContent, onChange }) => {
  const editor = useEditor({
    editable: true,
    extensions: [
      Document,
      StarterKit,
      TextStyle,
      FontSize,
      Color,
      Uppercase,
      Underline,
      Strike,
      Highlight,
      TextAlign.configure({ types: ["heading", "paragraph", "listItem"] }),
      Mention.configure({
        HTMLAttributes: {
          class: "mention-link",
        },
        suggestion: {
          char: "@",
          startOfLine: false,
          items: async ({ query }) => {
            if (!query) return [];
            try {
              const res = await axios.get(
                `${apiUrl}/api/users/search?query=${query}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                }
              );
              return res.data.map((user) => ({
                id: user._id,
                label: user.username,
              }));
            } catch (err) {
              console.error("Ошибка при поиске пользователей:", err);
              return [];
            }
          },
          render: () => {
            let component;

            return {
              onStart: (props) => {
                component = document.createElement("div");
                component.className = "mention-dropdown";
                props.items.forEach((item) => {
                  const el = document.createElement("div");
                  el.className = "mention-item";
                  el.textContent = `@${item.label}`;
                  el.onclick = () => props.command(item);
                  component.appendChild(el);
                });
                const coords = props.clientRect?.();
                if (coords) {
                  component.style.position = "absolute";
                  component.style.top = `${coords.top + window.scrollY + 30}px`;
                  component.style.left = `${coords.left + window.scrollX}px`;
                  component.style.zIndex = 9999;
                }
                document.body.appendChild(component);
              },
              onUpdate: (props) => {
                if (!component) return;
                component.innerHTML = "";
                props.items.forEach((item) => {
                  const el = document.createElement("div");
                  el.className = "mention-item";
                  el.textContent = `@${item.label}`;
                  el.onclick = () => props.command(item);
                  component.appendChild(el);
                });
              },
              onExit: () => component?.remove(),
            };
          },
        },
      }),
    ],
    content: initialContent || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getJSON());
    },
  });

  React.useEffect(() => {
    if (editor && initialContent !== undefined) {
      editor.commands.setContent(initialContent);
    }
  }, [initialContent, editor]);

  if (!editor) return null;

  const applyFontSize = (size) => {
    editor.chain().focus().setMark("textStyle", { fontSize: size }).run();
  };

  return (
    <div className="tiptap-editor-container">
      <div className="tiptap-toolbar">
        <select
          onChange={(e) => applyFontSize(e.target.value)}
          value={editor.getAttributes("textStyle").fontSize || ""}
          title="Размер шрифта"
          className="tiptap-toolbar-select"
        >
          <option value="" disabled>
            Размер
          </option>
          <option value="10px">10px</option>
          <option value="12px">12px</option>
          <option value="14px">14px</option>
          <option value="16px">16px</option>
          <option value="18px">18px</option>
          <option value="24px">24px</option>
          <option value="32px">32px</option>
        </select>
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`tiptap-toolbar-button ${
            editor.isActive("bold") ? "is-active" : ""
          }`}
          title="Жирный"
        >
          <b>B</b>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`tiptap-toolbar-button ${
            editor.isActive("italic") ? "is-active" : ""
          }`}
          title="Курсив"
        >
          <i>I</i>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`tiptap-toolbar-button ${
            editor.isActive("underline") ? "is-active" : ""
          }`}
          title="Подчеркнутый"
        >
          U
        </button>
        <button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`tiptap-toolbar-button ${
            editor.isActive("strike") ? "is-active" : ""
          }`}
          title="Зачеркнутый"
        >
          S
        </button>
        <button
          onClick={() => editor.chain().focus().toggleUppercase().run()}
          className={`tiptap-toolbar-button ${
            editor.isActive("uppercase") ? "is-active" : ""
          }`}
          title="Все заглавные"
        >
          Aa
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          className={`tiptap-toolbar-button ${
            editor.isActive({ textAlign: "left" }) ? "is-active" : ""
          }`}
          title="Влево"
        >
          ←
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          className={`tiptap-toolbar-button ${
            editor.isActive({ textAlign: "center" }) ? "is-active" : ""
          }`}
          title="По центру"
        >
          ⇆
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          className={`tiptap-toolbar-button ${
            editor.isActive({ textAlign: "right" }) ? "is-active" : ""
          }`}
          title="Вправо"
        >
          →
        </button>
        <label title="Цвет текста">
          <input
            type="color"
            onChange={(e) =>
              editor
                .chain()
                .focus()
                .setMark("textStyle", { color: e.target.value })
                .run()
            }
            style={{ padding: 0, border: "none", width: 24, height: 24 }}
          />
        </label>
      </div>
      <EditorContent editor={editor} className="tiptap-editor" />
    </div>
  );
};

export default TiptapEditor;
